/**
 * Server clock synchronisation.
 *
 * The server is the sole authority on cashouts, but the client must display
 * m(t) for the *same* t. We estimate the offset between our clock and the
 * server's from `ping` / `server:time` round trips (SNTP-style), then read the
 * time from a MONOTONIC base: `Date.now()` can jump backwards (NTP correction,
 * user changing the system clock) and a jump of even 200 ms is a visibly wrong
 * multiplier.
 *
 *   serverNow() = monotonic() + anchor
 *   anchor      = median( serverTime + rtt/2 - monotonicAtReceive )  over the
 *                 lower-latency half of the sample window
 *
 * Taking the median of the *fast* samples is the standard defence against
 * asymmetric latency spikes, and unlike a hard reject it still converges when
 * the connection degrades for good.
 */
import type { ServerTimeEvent } from "@crashx/shared";

export interface ClockOptions {
  /** Monotonic source in ms. Injected in tests. */
  monotonic?: () => number;
  /** Wall clock in ms, used for the ping key and the pre-sync fallback. */
  wallClock?: () => number;
}

interface Sample {
  rttMs: number;
  anchorMs: number;
}

export class Clock {
  /** Round trips kept for the median. ~8 covers 40 s at one ping / 5 s. */
  static readonly SAMPLE_SIZE = 8;
  /** Unanswered pings kept before the oldest is dropped. */
  static readonly MAX_PENDING = 16;
  /** An unanswered ping older than this is forgotten. */
  static readonly PENDING_TTL_MS = 30_000;
  /** Being this far *ahead* of a `round:tick` timestamp means we drifted. */
  static readonly DRIFT_AHEAD_MS = 1_500;
  /** Being *behind* a tick is impossible (one-way delay >= 0); allow only rounding. */
  static readonly DRIFT_BEHIND_MS = 100;

  private readonly monotonic: () => number;
  private readonly wallClock: () => number;
  /** clientSentAt → monotonic time at send. */
  private readonly pending = new Map<number, number>();
  private readonly samples: Sample[] = [];
  private anchor: number | null = null;
  private medianRtt = 0;

  constructor(options: ClockOptions = {}) {
    this.monotonic = options.monotonic ?? (() => performance.now());
    this.wallClock = options.wallClock ?? (() => Date.now());
  }

  /** True once at least one round trip completed. */
  synced(): boolean {
    return this.anchor !== null;
  }

  /** Current server time in unix ms. Falls back to the wall clock before the first sample. */
  now(): number {
    return this.anchor === null ? this.wallClock() : this.monotonic() + this.anchor;
  }

  /** Difference between server time and this device's wall clock, in ms. */
  get offsetMs(): number {
    return this.anchor === null ? 0 : this.now() - this.wallClock();
  }

  /** Median round-trip time of the current window, in ms. */
  get rttMs(): number {
    return this.medianRtt;
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  get pendingCount(): number {
    return this.pending.size;
  }

  /**
   * Register an outgoing `ping`. The returned value is what goes on the wire as
   * `clientSentAt`; the server echoes it back in `server:time`.
   */
  beginPing(): number {
    const sentAtMono = this.monotonic();
    let key = Math.floor(this.wallClock());
    // Two pings inside the same millisecond would collide on the map key.
    while (this.pending.has(key)) key += 1;

    this.prunePending(sentAtMono);
    this.pending.set(key, sentAtMono);
    return key;
  }

  /**
   * Feed a `server:time` reply.
   * @returns true if it matched a ping we sent and was used.
   */
  acceptSample(payload: ServerTimeEvent): boolean {
    const sentAtMono = this.pending.get(payload.clientSentAt);
    if (sentAtMono === undefined) return false; // unknown or already consumed
    this.pending.delete(payload.clientSentAt);

    const receivedAtMono = this.monotonic();
    const rttMs = Math.max(0, receivedAtMono - sentAtMono);
    // Assume a symmetric path: the server stamped the reply rtt/2 ago.
    const anchorMs = payload.serverTime + rttMs / 2 - receivedAtMono;

    this.samples.push({ rttMs, anchorMs });
    if (this.samples.length > Clock.SAMPLE_SIZE) this.samples.shift();
    this.recompute();
    return true;
  }

  /**
   * Cheap drift check against a `round:tick`, which carries no round-trip
   * information. A tick can only reach us *after* the server stamped it, so a
   * tick timestamped in our future means our clock is behind — always an error.
   * @returns true when an extra `ping` should be sent to re-sync.
   */
  checkDrift(serverTime: number): boolean {
    if (this.anchor === null) return false;
    const drift = this.now() - serverTime;
    return drift < -Clock.DRIFT_BEHIND_MS || drift > Clock.DRIFT_AHEAD_MS;
  }

  /** Forget everything — call on socket disconnect so a reconnect re-syncs. */
  reset(): void {
    this.pending.clear();
    this.samples.length = 0;
    this.anchor = null;
    this.medianRtt = 0;
  }

  private recompute(): void {
    const rtts = this.samples.map((s) => s.rttMs).sort((a, b) => a - b);
    const medianRtt = median(rtts);
    // The min-rtt sample always passes, so `fast` is never empty.
    const fast = this.samples.filter((s) => s.rttMs <= medianRtt).map((s) => s.anchorMs);
    fast.sort((a, b) => a - b);
    this.medianRtt = medianRtt;
    this.anchor = median(fast);
  }

  private prunePending(nowMono: number): void {
    for (const [key, sentAt] of this.pending) {
      if (nowMono - sentAt > Clock.PENDING_TTL_MS) this.pending.delete(key);
    }
    while (this.pending.size >= Clock.MAX_PENDING) {
      const oldest = this.pending.keys().next();
      if (oldest.done === true) break;
      this.pending.delete(oldest.value);
    }
  }
}

/** Median of an already-sorted, non-empty array. */
function median(sorted: readonly number[]): number {
  const n = sorted.length;
  if (n === 0) return 0;
  const mid = n >> 1;
  if (n % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}
